import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { COLORS } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

const DateFilterContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DateInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 8px;
  padding: 8px 12px;
  min-width: 200px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.COLA};
  }

  &:focus-within {
    border-color: ${COLORS.COLA};
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const DateInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${COLORS.COLA};
  flex: 1;
  cursor: pointer;

  &::placeholder {
    color: ${COLORS.GRAY};
  }
`;

const CalendarIcon = styled.div`
  width: 16px;
  height: 16px;
  margin-left: 8px;
  color: ${COLORS.GRAY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClearButton = styled.div`
  width: 16px;
  height: 16px;
  margin-left: 8px;
  color: ${COLORS.GRAY};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS.COLA};
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: ${props => (props.isOpen ? 'flex' : 'none')};
  margin-top: 4px;
  min-width: 400px;
  max-width: 500px;
`;

const PresetPanel = styled.div`
  flex: 1;
  padding: 16px;
  border-right: 1px solid ${COLORS.GRAY_LIGHT};
  min-width: 180px;
`;

const CalendarPanel = styled.div`
  flex: 1;
  padding: 16px;
  min-width: 220px;
`;

const PresetOption = styled.div`
  padding: 8px 12px;
  margin-bottom: 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: ${COLORS.COLA};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${COLORS.GRAY_LIGHT};
  }

  ${props =>
    props.isSelected &&
    `
    background-color: ${COLORS.COLA};
    color: ${COLORS.WHITE};
  `}
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const MonthYear = styled(Text)`
  font-weight: 600;
  font-size: 16px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${COLORS.COLA};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${COLORS.GRAY_LIGHT};
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayHeader = styled.div`
  padding: 8px 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.GRAY};
`;

const DayCell = styled.div`
  padding: 8px 4px;
  text-align: center;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  color: ${props => {
    if (props.isSelected) return COLORS.WHITE;
    if (props.isCurrentMonth) return COLORS.COLA;
    return COLORS.GRAY;
  }};
  background-color: ${props => {
    if (props.isSelected) return COLORS.COLA;
    if (props.isToday) return COLORS.GRAY_LIGHT;
    return 'transparent';
  }};

  &:hover {
    background-color: ${props => (props.isSelected ? COLORS.COLA : COLORS.GRAY_LIGHT)};
  }
`;

const DateRangeFilter = ({
  onDateRangeChange,
  placeholder = 'Select a date range',
  value = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  const containerRef = useRef(null);

  const presets = [
    {
      label: 'Today',
      getDates: () => {
        const today = new Date();
        return { start: today, end: today };
      },
    },
    {
      label: 'Yesterday',
      getDates: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: yesterday };
      },
    },
    {
      label: 'Last 7 days',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 6);
        return { start, end };
      },
    },
    {
      label: 'Last 14 days',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 13);
        return { start, end };
      },
    },
    {
      label: 'Last 30 days',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 29);
        return { start, end };
      },
    },
    {
      label: 'This Week',
      getDates: () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return { start, end };
      },
    },
    {
      label: 'Last Week',
      getDates: () => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay() - 7);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return { start, end };
      },
    },
    {
      label: 'This Month',
      getDates: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start, end };
      },
    },
    {
      label: 'Last Month',
      getDates: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start, end };
      },
    },
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Sync internal state with external value prop
  useEffect(() => {
    if (value) {
      setSelectedStartDate(value.startDate || null);
      setSelectedEndDate(value.endDate || null);
    } else {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setSelectedPreset(null);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDate = date => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateRange = (start, end) => {
    if (!start && !end) return '';
    if (start && !end) return formatDate(start);
    if (start && end) {
      if (start.getTime() === end.getTime()) {
        return formatDate(start);
      }
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    return '';
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const handlePresetSelect = preset => {
    const { start, end } = preset.getDates();
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    setSelectedPreset(preset.label);
    setIsSelectingRange(false);

    if (onDateRangeChange) {
      onDateRangeChange({
        startDate: start,
        endDate: end,
        preset: preset.label,
      });
    }

    setIsOpen(false);
  };

  const handleDateClick = date => {
    if (!isSelectingRange || !selectedStartDate) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      setIsSelectingRange(true);
      setSelectedPreset(null);
    } else {
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
      } else {
        setSelectedEndDate(date);
      }
      setIsSelectingRange(false);

      if (onDateRangeChange) {
        onDateRangeChange({
          startDate: selectedStartDate,
          endDate: date < selectedStartDate ? selectedStartDate : date,
          preset: null,
        });
      }

      setIsOpen(false);
    }
  };

  const navigateMonth = direction => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleClear = e => {
    e.stopPropagation(); // Prevent opening the dropdown
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectedPreset(null);
    setIsSelectingRange(false);

    if (onDateRangeChange) {
      onDateRangeChange(null);
    }
  };

  const isDateSelected = date => {
    if (!selectedStartDate) return false;
    if (selectedStartDate && !selectedEndDate) {
      return date.getTime() === selectedStartDate.getTime();
    }
    return (
      date.getTime() === selectedStartDate.getTime() ||
      date.getTime() === selectedEndDate.getTime()
    );
  };

  const isToday = date => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const calendarDays = getCalendarDays();

  return (
    <DateFilterContainer ref={containerRef}>
      <DateInputWrapper onClick={() => setIsOpen(!isOpen)}>
        <DateInput
          type="text"
          placeholder={placeholder}
          value={formatDateRange(selectedStartDate, selectedEndDate)}
          readOnly
        />
        {selectedStartDate || selectedEndDate ? (
          <ClearButton onClick={handleClear}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </ClearButton>
        ) : (
          <CalendarIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
            </svg>
          </CalendarIcon>
        )}
      </DateInputWrapper>

      <DropdownContainer isOpen={isOpen}>
        <PresetPanel>
          <Text variant="body2" style={{ marginBottom: '12px', fontWeight: '600' }}>
            Quick Select
          </Text>
          {presets.map(preset => (
            <PresetOption
              key={preset.label}
              isSelected={selectedPreset === preset.label}
              onClick={() => handlePresetSelect(preset)}
            >
              {preset.label}
            </PresetOption>
          ))}
        </PresetPanel>

        <CalendarPanel>
          <CalendarHeader>
            <NavButton onClick={() => navigateMonth(-1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </NavButton>
            <MonthYear variant="body1">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </MonthYear>
            <NavButton onClick={() => navigateMonth(1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </NavButton>
          </CalendarHeader>

          <CalendarGrid>
            {daysOfWeek.map(day => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
            {calendarDays.map((date, index) => (
              <DayCell
                key={index}
                isCurrentMonth={date.getMonth() === currentMonth.getMonth()}
                isSelected={isDateSelected(date)}
                isToday={isToday(date)}
                onClick={() => handleDateClick(date)}
              >
                {date.getDate()}
              </DayCell>
            ))}
          </CalendarGrid>
        </CalendarPanel>
      </DropdownContainer>
    </DateFilterContainer>
  );
};

DateRangeFilter.propTypes = {
  onDateRangeChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.object,
};

export default DateRangeFilter;
